import Carousel from '@/components/carousel/Carousel'
import CurrentEvent from '@/components/currentEvent/CurrentEvent'
import Divider from '@/components/divider/Divider'
import Image from '@/components/image/Image'
import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import YouTubeIcon from '@mui/icons-material/YouTube'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid2'
import Link from '@mui/material/Link'
import { useTheme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import React from 'react'

const footerImages = [
  '/images/statics/cleaning.jpg',
  '/images/statics/fika-3.jpg',
  '/images/statics/recording.jpg',
  '/images/history/ostersjofestivalen-4.jpg',
]

const latestNewImages = [
  '/images/history/dance-macabre-2026/dance-macabre-2026-1.jpg',
  '/images/history/dance-macabre-2026/dance-macabre-2026-2.jpg',
  '/images/history/dance-macabre-2026/dance-macabre-2026-3.jpg',
  '/images/history/dance-macabre-2026/dance-macabre-2026-4.jpg',
]

const socialLinks = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/scoutmusikkaren/',
    icon: <InstagramIcon />,
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/scoutmusikkaren/',
    icon: <FacebookIcon />,
  },
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/@KarlshamnsScoutmusikk%C3%A5r',
    icon: <YouTubeIcon />,
  },
]

const Home: React.FC = () => {
  const theme = useTheme()
  // Returnerar true om skärmen är mindre än 'md' (standard är 900px i MUI)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  return (
    <Grid
      container
      direction="column"
      justifyContent="center"
      alignItems="center"
      textAlign="center"
    >
      <Grid size={{ xs: 11, md: 10 }} sx={{ py: { xs: 3, md: 5 } }}>
        <Typography
          variant="h1"
          fontWeight="700"
          sx={{
            maxWidth: 760,
            mx: 'auto',
            fontSize: { xs: '1.65rem', md: '2.5rem' },
            lineHeight: 1.2,
          }}
        >
          Välkommen till Sveriges äldsta Scoutmusikkår!
        </Typography>
        <p>Följ med på konserter, repetitioner och livet i musikkåren.</p>
        <Box
          component="nav"
          aria-label="Följ oss i sociala medier"
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 1.25,
          }}
        >
          {socialLinks.map(({ label, href, icon }) => (
            <Button
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${label} (öppnas i ny flik)`}
              startIcon={icon}
              variant="contained"
              sx={{
                minHeight: 44,
                px: 2,
                color: '#FFFFFF',
                '&.Mui-focusVisible': {
                  outline: '2px solid',
                  outlineColor: 'text.primary',
                  outlineOffset: 3,
                },
              }}
            >
              {label}
            </Button>
          ))}
        </Box>
      </Grid>

      <Grid size={{ xs: 11, md: 10 }}>
        <Image
          src="/images/statics/groupimage-2024.jpg"
          alt="Gruppbild"
          loading="eager"
          skeletonHeight={821}
        />
      </Grid>

      <Divider size={{ xs: 11, md: 8 }} />

      <CurrentEvent />

      <Divider size={{ xs: 11, md: 8 }} />

      <Grid size={{ xs: 11, md: 6 }}>
        <h2>Senaste nytt</h2>
        <h3>Dance Macabre – vilken kväll!</h3>
        <p style={{ textAlign: 'start' }}>
          Rök, rysningar och ett rejält musikaliskt ös! Med Dance Macabre fyllde
          vi Lokstallarna med både toner och tät dimma. Ett stort tack till alla
          er som kom, lyssnade och gjorde kvällen till en riktig höjdare!
        </p>
        {isMobile ? (
          <Carousel images={latestNewImages} />
        ) : (
          <Grid
            container
            size={{ xs: 12 }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            {latestNewImages.map((src, index) => (
              <Grid key={index} size={{ xs: 12, md: 6 }}>
                <Image src={src} alt={`Östersjö ${index + 1}`} />
              </Grid>
            ))}
          </Grid>
        )}
      </Grid>

      <Divider size={{ xs: 11, md: 8 }} />

      <Grid size={{ xs: 11, md: 6 }}>
        <h2>Detta är Karlshamns Scoutmusikkår</h2>
        <p style={{ textAlign: 'start' }}>
          Vi är en ideell förening där musik och gemenskap står i centrum. Vi
          spelar på konserter, parader och andra evenemang - och har roligt
          tillsammans både på och utanför scenen.
        </p>
      </Grid>
      <Grid size={{ xs: 11, md: 6 }}>
        <h2>
          <Link href="/contact">Klicka här för att kontakta oss!</Link>
        </h2>
      </Grid>
      <Grid size={{ xs: 11, md: 10 }}>
        <Image
          src="/images/history/gilla-karlshamn-2026/gilla-karlshamn-2026-1.jpg"
          alt="GillaKarlshamn"
          photographer="Cecilia Jägenmark"
          caption={
            <>
              <Link
                href="https://gillakarlshamn.se/gilla-karlshamngalan/slutsalt-till-galan-igen/"
                sx={{ ml: 1 }}
              >
                Röj på Gilla Karlshamn galan 2026
              </Link>
            </>
          }
        />
      </Grid>

      <Divider size={{ xs: 11, md: 8 }} />

      <Grid sx={{ marginBottom: '50px' }}>
        <Carousel images={footerImages} />
      </Grid>
    </Grid>
  )
}

export default Home
